// Fields: a label above, the field, a hint below, and what's wrong in words beside an icon. Each is labelled and
// described for a screen reader, and says it's invalid when it is.
import {
  useId,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { IconButton } from './Button';
import { cx } from './cx';
import s from './Field.module.css';
import { formatPairCode } from './text';
import {
  CaretUpDownIcon,
  CheckIcon,
  EyeIcon,
  EyeSlashIcon,
  MagnifyingGlassIcon,
  WarningCircleIcon,
  XCircleIcon,
} from './icons';

interface Frame {
  label: ReactNode;
  /** Shown under the field, and read with it. */
  hint?: ReactNode;
  /** What's wrong with it, or nothing. */
  error?: ReactNode;
  /** Says "optional" beside the label. */
  optional?: boolean;
  /** The label is read but not shown: when what's around it says what the field is. */
  hideLabel?: boolean;
  className?: string;
}

/** The label, hint and error around a field, and the ids that join them to it. */
function FieldFrame({
  id,
  label,
  hint,
  error,
  optional,
  hideLabel,
  className,
  children,
}: Frame & { id: string; children: ReactNode }) {
  return (
    <div className={cx(s.field, error ? s.invalid : null, className)}>
      <label htmlFor={id} className={cx(s.label, hideLabel && 'visually-hidden')}>
        {label}
        {optional && <span className={s.optional}> (optional)</span>}
      </label>
      {children}
      {hint && (
        <p id={`${id}-hint`} className={s.hint}>
          {hint}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} className={s.error}>
          <WarningCircleIcon size={16} weight="fill" aria-hidden="true" />
          <span>{error}</span>
        </p>
      )}
    </div>
  );
}

function described(id: string, { hint, error }: Pick<Frame, 'hint' | 'error'>, more?: string): string | undefined {
  const ids = [error ? `${id}-error` : null, hint ? `${id}-hint` : null, more ?? null].filter(Boolean);
  return ids.length > 0 ? ids.join(' ') : undefined;
}

type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> & Frame;

export function TextField({
  label,
  hint,
  error,
  optional,
  hideLabel,
  className,
  prefix,
  suffix,
  trailing,
  id: given,
  ref,
  ...input
}: InputProps & {
  /** An icon before the text. */
  prefix?: ReactNode;
  /** A unit after the text: "km", "ms". */
  suffix?: ReactNode;
  /** A button at the end. */
  trailing?: ReactNode;
  ref?: Ref<HTMLInputElement>;
}) {
  const made = useId();
  const id = given ?? made;
  return (
    <FieldFrame {...{ id, label, hint, error, optional, hideLabel, className }}>
      <div className={s.control}>
        {prefix && (
          <span className={s.prefixIcon} aria-hidden="true">
            {prefix}
          </span>
        )}
        <input
          ref={ref}
          id={id}
          aria-invalid={error ? true : undefined}
          aria-describedby={described(id, { hint, error })}
          {...input}
        />
        {suffix && (
          <span className={s.affix} aria-hidden="true">
            {suffix}
          </span>
        )}
        {trailing && <span className={s.trailing}>{trailing}</span>}
      </div>
    </FieldFrame>
  );
}

export function TextArea({
  label,
  hint,
  error,
  optional,
  hideLabel,
  className,
  id: given,
  ...area
}: TextareaHTMLAttributes<HTMLTextAreaElement> & Frame) {
  const made = useId();
  const id = given ?? made;
  return (
    <FieldFrame {...{ id, label, hint, error, optional, hideLabel, className }}>
      <div className={s.control}>
        <textarea
          id={id}
          aria-invalid={error ? true : undefined}
          aria-describedby={described(id, { hint, error })}
          {...area}
        />
      </div>
    </FieldFrame>
  );
}

export interface Rule {
  /** What it needs, as read: "At least 10 characters". */
  label: string;
  met: boolean;
}

/** A password: hidden until Show is pressed, with the pool's rules ticking off as they're met. */
export function SecureField({
  rules,
  ...props
}: Omit<InputProps, 'type'> & { rules?: readonly Rule[]; ref?: Ref<HTMLInputElement> }) {
  const [shown, setShown] = useState(false);
  const made = useId();
  const id = props.id ?? made;
  const rulesId = `${id}-rules`;
  return (
    <div className={s.field}>
      <TextField
        {...props}
        id={id}
        type={shown ? 'text' : 'password'}
        spellCheck={false}
        autoCapitalize="none"
        aria-describedby={described(id, props, rules ? rulesId : undefined)}
        trailing={
          <IconButton
            size="small"
            label={shown ? 'Hide password' : 'Show password'}
            aria-pressed={shown}
            icon={shown ? <EyeSlashIcon size={18} /> : <EyeIcon size={18} />}
            onClick={() => setShown((x) => !x)}
          />
        }
      />
      {rules && (
        <ul id={rulesId} className={s.rules} aria-label="What the password needs">
          {rules.map((rule) => (
            <li key={rule.label}>
              {rule.met ? (
                <CheckIcon className={s.met} size={14} weight="bold" aria-hidden="true" />
              ) : (
                <span aria-hidden="true" className={s.ruleDot}>
                  •
                </span>
              )}
              <span>
                {rule.label}
                <span className="visually-hidden">{rule.met ? ': done' : ': not yet'}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** A one-time code from an email or an authenticator app: six digits, which the phone can fill in itself. */
export function CodeField({
  length = 6,
  ...props
}: Omit<InputProps, 'type' | 'inputMode'> & { length?: number; ref?: Ref<HTMLInputElement> }) {
  return (
    <TextField
      className={s.code}
      inputMode="numeric"
      autoComplete="one-time-code"
      pattern={`[0-9]{${length}}`}
      spellCheck={false}
      {...props}
      onChange={(e) => {
        e.target.value = e.target.value.replace(/\D/g, '').slice(0, length);
        props.onChange?.(e);
      }}
    />
  );
}

export function PairCodeField(props: Omit<InputProps, 'type'> & { ref?: Ref<HTMLInputElement> }) {
  return (
    <TextField
      className={s.code}
      inputMode="text"
      autoCapitalize="characters"
      autoComplete="off"
      spellCheck={false}
      placeholder="XXXX-XXXX"
      {...props}
      onChange={(e) => {
        e.target.value = formatPairCode(e.target.value);
        props.onChange?.(e);
      }}
    />
  );
}

/** A search: what it looks through is its label, read but not shown. Escape clears it. */
export function SearchField({
  value,
  onValueChange,
  label,
  placeholder = 'Search',
  ...rest
}: Omit<InputProps, 'value' | 'onChange' | 'type' | 'label'> & {
  value: string;
  onValueChange: (value: string) => void;
  label: string;
}) {
  return (
    <TextField
      className={s.search}
      type="search"
      label={label}
      hideLabel
      placeholder={placeholder}
      prefix={<MagnifyingGlassIcon size={18} weight="bold" />}
      value={value}
      onChange={(e) => onValueChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && value) {
          e.preventDefault();
          onValueChange('');
        }
      }}
      trailing={
        value ? (
          <IconButton
            size="small"
            label="Clear"
            icon={<XCircleIcon size={18} weight="fill" />}
            onClick={() => onValueChange('')}
          />
        ) : undefined
      }
      enterKeyHint="search"
      autoComplete="off"
      {...rest}
    />
  );
}

/** A choice of several, as the browser offers it. */
export function Select({
  label,
  hint,
  error,
  optional,
  hideLabel,
  className,
  id: given,
  children,
  ...select
}: SelectHTMLAttributes<HTMLSelectElement> & Frame) {
  const made = useId();
  const id = given ?? made;
  return (
    <FieldFrame {...{ id, label, hint, error, optional, hideLabel, className }}>
      <div className={s.control}>
        <select
          id={id}
          aria-invalid={error ? true : undefined}
          aria-describedby={described(id, { hint, error })}
          {...select}
        >
          {children}
        </select>
        <CaretUpDownIcon className={s.selectCaret} size={16} weight="bold" aria-hidden="true" />
      </div>
    </FieldFrame>
  );
}
